import { Search, ChevronsDownUp, ChevronsUpDown, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { getTypeMeta } from './catalog-meta';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';

interface PricingToolbarProps {
  search: string;
  onSearch: (v: string) => void;
  ccode: string;
  onCcode: (v: string) => void;
  currencies: string[];
  totid: number | 'all';
  onTotid: (v: number | 'all') => void;
  typeOptions: number[];
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onCreate: () => void;
}

/** Search, type filter, currency selector, create + expand/collapse controls. */
export const PricingToolbar = ({
  search,
  onSearch,
  ccode,
  onCcode,
  currencies,
  totid,
  onTotid,
  typeOptions,
  onExpandAll,
  onCollapseAll,
  onCreate,
}: PricingToolbarProps) => {
  const { allowed: canManage, reason } = useGate(PERMISSIONS.promotion.edit);
  return (
  <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
    <div className="relative flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <Input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search plans by name…"
        className="pl-9"
      />
    </div>

    <select
      value={String(totid)}
      onChange={(e) => onTotid(e.target.value === 'all' ? 'all' : Number(e.target.value))}
      className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="all">All types</option>
      {typeOptions.map((t) => (
        <option key={t} value={t}>
          {getTypeMeta(t).label}
        </option>
      ))}
    </select>

    <select
      value={ccode}
      onChange={(e) => onCcode(e.target.value)}
      className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {currencies.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>

    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onExpandAll}
        title="Expand all"
        className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
      >
        <ChevronsUpDown className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onCollapseAll}
        title="Collapse all"
        className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
      >
        <ChevronsDownUp className="h-4 w-4" />
      </button>
    </div>

    <button
      type="button"
      disabled={!canManage}
      title={canManage ? undefined : reason}
      onClick={() => canManage && onCreate()}
      className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
    >
      <Plus className="h-4 w-4" />
      Create plan
    </button>
  </div>
  );
};
